import { useState } from 'react';
import { cn } from '@/shared/lib/utils/cn';

const SIZE = {
  sm: 'h-7 w-7 rounded-lg text-[10px]',
  md: 'h-9 w-9 rounded-xl text-xs',
  lg: 'h-12 w-12 rounded-xl text-sm',
  xl: 'h-20 w-20 rounded-2xl text-2xl',
} as const;

export type AvatarSize = keyof typeof SIZE;

export interface AvatarProps {
  initials: string;
  /** Tailwind background color class, e.g. `bg-amber-500`. */
  color: string;
  /** Public profile-picture URL. Falls back to initials if absent or unavailable. */
  src?: string | null;
  alt?: string;
  size?: AvatarSize;
  className?: string;
}

/** Profile picture with an initials fallback in the user's chosen color. */
export function Avatar({ initials, color, src, alt, size = 'md', className }: AvatarProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = Boolean(src) && failedSrc !== src;

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden font-bold text-white',
        SIZE[size],
        color,
        className,
      )}
      role={alt ? 'img' : undefined}
      aria-label={alt}
      aria-hidden={alt ? undefined : true}
    >
      {showImage ? (
        <img
          src={src ?? undefined}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
          onError={() => setFailedSrc(src ?? null)}
        />
      ) : (
        initials
      )}
    </div>
  );
}
